import { client } from './client/Client';

interface CardTransactionAmount {
  amount: number;
  currencyCode: string;
}

interface CardTransactionTotalAmounts {
  merchantSettled?: CardTransactionAmount;
  merchantAuthorized?: CardTransactionAmount;
  cardholderAuthorized?: CardTransactionAmount;
  cardholderSettled?: CardTransactionAmount;
}

interface CardAcceptorDetails {
  name?: string;
}

export interface CardTransaction {
  id: string;
  cardProgramId: string;
  cardAccountId: string;
  cardId: string;
  cardTokenId?: string;
  totalAmounts?: CardTransactionTotalAmounts;
  cardAcceptorDetails?: CardAcceptorDetails;
  networkTransactionId?: string;
  originalCardTransactionId?: string;
  authorizationExpiresAt?: Date;
  type?: string;
  createdAt: Date;
}

interface ListCardTransactionsResponse {
  cardTransactions: CardTransaction[];
  hasMore: boolean;
}

export interface ListCardTransactionsParams {
  limit?: number;
  startingAfter?: string;
  endingBefore?: string;
  cardProgramId?: string;
  cardAccountId?: string;
  cardId?: string;
  cardTokenId?: string;
}

const convertCardTransactionResponse = (response: any): CardTransaction => {
  const mapAmount = (amount: any): CardTransactionAmount | undefined => {
    if (!amount) return undefined;

    const rawAmount = amount.amount;
    const parsedAmount = typeof rawAmount === 'string' ? parseInt(rawAmount, 10) : rawAmount;
    const amountValue = Number.isFinite(parsedAmount) ? parsedAmount : undefined;
    const currencyCode = amount.currencyCode ?? amount.currency_code;
    if (typeof amountValue !== 'number' || !currencyCode) return undefined;

    return {
      amount: amountValue,
      currencyCode,
    };
  };

  const rawTotalAmounts = response.totalAmounts ?? response.total_amounts;
  const totalAmounts: CardTransactionTotalAmounts | undefined = rawTotalAmounts
    ? {
        merchantSettled: mapAmount(rawTotalAmounts.merchantSettled ?? rawTotalAmounts.merchant_settled),
        merchantAuthorized: mapAmount(rawTotalAmounts.merchantAuthorized ?? rawTotalAmounts.merchant_authorized),
        cardholderAuthorized: mapAmount(rawTotalAmounts.cardholderAuthorized ?? rawTotalAmounts.cardholder_authorized),
        cardholderSettled: mapAmount(rawTotalAmounts.cardholderSettled ?? rawTotalAmounts.cardholder_settled),
      }
    : undefined;

  const rawDate = response.createdAt ?? response.created_at;
  const rawAuthorizationExpiresAt = response.authorizationExpiresAt ?? response.authorization_expires_at;
  const cardAcceptorDetails = response.cardAcceptorDetails ?? response.card_acceptor_details;

  return {
    id: response.id,
    cardProgramId: response.cardProgramId ?? response.card_program_id,
    cardAccountId: response.cardAccountId ?? response.card_account_id,
    cardId: response.cardId ?? response.card_id,
    cardTokenId: response.cardTokenId ?? response.card_token_id,
    totalAmounts,
    cardAcceptorDetails: cardAcceptorDetails
      ? {
          name: cardAcceptorDetails.name,
        }
      : undefined,
    // Preserve raw values as strings (e.g. `088800000123456`) to avoid losing leading zeroes.
    networkTransactionId: response.networkTransactionId ?? response.network_transaction_id,
    originalCardTransactionId: response.originalCardTransactionId ?? response.original_card_transaction_id,
    authorizationExpiresAt: rawAuthorizationExpiresAt ? new Date(rawAuthorizationExpiresAt) : undefined,
    type: response.type,
    createdAt: rawDate ? new Date(rawDate) : new Date(0),
  };
};

export class CardTransactionRepository {
  static async get(cardTransactionId: string): Promise<CardTransaction> {
    return client.get<undefined, CardTransaction>(`/cards/transactions/${cardTransactionId}`).then((response) => {
      return convertCardTransactionResponse(response);
    });
  }

  static async listCardTransactions(query?: ListCardTransactionsParams): Promise<ListCardTransactionsResponse> {
    return client
      .get<ListCardTransactionsParams, ListCardTransactionsResponse>('/cards/transactions', { query })
      .then((response) => ({
        cardTransactions: Array.isArray(response?.cardTransactions)
          ? response.cardTransactions.map((transaction) => convertCardTransactionResponse(transaction))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }
}
